import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BookOpen, FileDown, KeyRound, LogOut, Mail, ShoppingCart } from "lucide-react";
import { Reveal, Eyebrow } from "@/components/Reveal";
import { useLang } from "@/i18n";

const DEALER_RANGE_START = 10001001;
const DEALER_RANGE_END = 10009999;
const SESSION_HOURS = 12;

const DOWNLOADS = [
    {
        id: "shop",
        type: "route",
        icon: ShoppingCart,
        de: {
            title: "Webshop: direkt online bestellen",
            desc: "Artikel aus dem Fachhandelskatalog in den Warenkorb legen und die Bestellung direkt an unseren Vertrieb senden.",
            cta: "Zum Bestellshop",
        },
        en: {
            title: "Web shop: order directly online",
            desc: "Add items from the trade catalogue to your cart and send the order straight to our sales team.",
            cta: "Open shop",
        },
        url: "/haendler/shop",
        testid: "dealer-shop-link",
    },
    {
        id: "pageflip",
        type: "route",
        de: {
            title: "Gesamtkatalog & Händlerpreise online blättern",
            desc: "Der vollständige Katalog inklusive Händlerpreise als Umblätter-Buch — mit Suchfunktion, direkt im Browser.",
            cta: "Buch öffnen",
        },
        en: {
            title: "Full catalogue & dealer prices — browse online",
            desc: "The complete catalogue including dealer prices as a page-turning book — with search, right in your browser. (Content in German)",
            cta: "Open book",
        },
        url: "/pageflip-preise",
        testid: "dealer-pageflip-link",
    },
    {
        id: "katalog",
        type: "link",
        de: {
            title: "Gesamtkatalog & Händlerpreise (PDF)",
            desc: "Der vollständige Katalog inklusive Händlerpreise — zum Durchblättern, Ausdrucken und als Anhaltspunkt für Ihre Kalkulation.",
        },
        en: {
            title: "Full catalogue & dealer prices (PDF)",
            desc: "The complete catalogue including dealer prices — for browsing, printing and as a basis for your calculations. (Content in German)",
        },
        url: "/datenblaetter/katalog_preise.pdf",
    },
    {
        id: "bestand",
        type: "link",
        de: {
            title: "Bestandsliste als Excel-Datei",
            desc: "Alle Artikel des Fachhandelskatalogs mit Typ, Bezeichnung, Produktgruppe und Nettopreisen — als Tabelle zum Sortieren und Filtern für Ihre Kalkulation.",
        },
        en: {
            title: "Stock list as Excel file",
            desc: "All items from the trade catalogue with type, description, product group and net prices — as a sortable spreadsheet for your calculations.",
        },
        url: "/bestand.xlsx",
        ctaDe: "Excel öffnen",
        ctaEn: "Open Excel",
        testid: "dealer-download-xlsx-link",
    },
    {
        id: "preise",
        type: "contact",
        de: {
            title: "Preislisten & Händlerpreise",
            desc: "Ihre aktuellen Preislisten mit Händlerpreisen erhalten Sie persönlich — per E-Mail oder unter +49 (0) 9195 / 9404-0.",
            subject: "Preislisten-Anfrage Händler",
        },
        en: {
            title: "Price lists & dealer prices",
            desc: "Your current price lists with dealer prices are available personally — by email or on +49 (0) 9195 / 9404-0.",
            subject: "Price list enquiry - dealer",
        },
        mailto: "vertrieb@satmueller.de",
    },
];

export default function DealerLogin() {
    const [kdnInput, setKdnInput] = useState("");
    const [error, setError] = useState("");
    const [session, setSession] = useState(null);
    const { t } = useLang();

    useEffect(() => {
        try {
            const raw = localStorage.getItem("dealer_login");
            if (!raw) return;
            const s = JSON.parse(raw);
            if (s && s.kdn && s.exp > Date.now()) setSession(s);
            else localStorage.removeItem("dealer_login");
        } catch {
            /* ignore */
        }
    }, []);

    const login = (e) => {
        e.preventDefault();
        const cleaned = kdnInput.replace(/\D/g, "");
        if (!cleaned) {
            setError(t("Bitte geben Sie Ihre Kundennummer ein.", "Please enter your customer number."));
            return;
        }
        const kdn = parseInt(cleaned, 10);
        if (!(DEALER_RANGE_START <= kdn && kdn <= DEALER_RANGE_END)) {
            setError(t(
                "Diese Kundennummer hat keinen Zugang. Bitte wenden Sie sich an vertrieb@satmueller.de",
                "This customer number has no access. Please contact vertrieb@satmueller.de"
            ));
            return;
        }
        const s = { kdn: String(kdn), exp: Date.now() + SESSION_HOURS * 3600 * 1000 };
        try {
            localStorage.setItem("dealer_login", JSON.stringify(s));
        } catch {
            /* ignore */
        }
        setSession(s);
        setError("");
        setKdnInput("");
    };

    const logout = () => {
        try {
            localStorage.removeItem("dealer_login");
        } catch {
            /* ignore */
        }
        setSession(null);
        setError("");
    };

    return (
        <section id="haendler" data-testid="haendler" className="scroll-mt-24 py-16 sm:py-24">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
                <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
                    <Reveal>
                        <Eyebrow>{t("Händler-Login", "Dealer login")}</Eyebrow>
                        <h2 className="mt-5 font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-900">
                            {t("Händlerbereich: Katalog & Preislisten.", "Dealer area: catalogue & price lists.")}
                        </h2>
                        <p className="mt-4 text-slate-600 leading-relaxed text-sm sm:text-base">
                            {t(
                                "Bitte geben Sie Ihre Kundennummer ein. Angemeldete Fachhändler erreichen unseren Gesamtkatalog und können aktuelle Preislisten direkt anfragen — kein Passwort nötig.",
                                "Please enter your customer number. Registered trade dealers get access to our full catalogue and can request current price lists directly — no password needed."
                            )}
                        </p>
                        <p className="mt-3 text-sm text-slate-500">
                            {t("Kein Zugang? Sprechen Sie uns an:", "No access? Talk to us:")}{" "}
                            <a href="tel:+49919594040" data-testid="haendler-info-phone" className="font-semibold text-red-700 hover:text-red-800">
                                +49 (0) 9195 / 9404-0
                            </a>
                        </p>
                    </Reveal>

                    <Reveal delay={0.1}>
                        {session ? (
                            <div data-testid="dealer-protected-panel" className="rounded-2xl bg-white border border-slate-200 shadow-sm p-7">
                                <div className="flex items-center justify-between gap-4 border-b border-slate-200 pb-5">
                                    <span className="inline-flex items-center gap-2.5">
                                        <span className="inline-flex items-center justify-center h-10 w-10 rounded-full bg-red-50 border border-red-100 text-red-700">
                                            <KeyRound className="h-5 w-5" />
                                        </span>
                                        <span className="flex flex-col leading-tight">
                                            <span className="text-[11px] font-mono uppercase tracking-widest text-slate-500">{t("Angemeldet", "Signed in")}</span>
                                            <span className="font-semibold text-slate-900" data-testid="dealer-kundennummer">
                                                {t("Händler", "Dealer")} {session.kdn}
                                            </span>
                                        </span>
                                    </span>
                                    <button
                                        onClick={logout}
                                        data-testid="dealer-logout"
                                        className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 hover:border-red-400 hover:text-red-700 text-slate-600 text-sm font-semibold px-4 py-2 transition-colors"
                                    >
                                        <LogOut className="h-4 w-4" />
                                        {t("Abmelden", "Sign out")}
                                    </button>
                                </div>
                                <div className="mt-5 space-y-3">
                                    {DOWNLOADS.map((d) => (
                                        <div
                                            key={d.id}
                                            data-testid={`dealer-item-${d.id}`}
                                            className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 rounded-xl border border-slate-200 bg-slate-50/60 p-4"
                                        >
                                            <div className="min-w-0">
                                                <p className="font-semibold text-slate-900 text-sm">{t(d.de.title, d.en.title)}</p>
                                                <p className="text-xs text-slate-600 mt-1 leading-relaxed">{t(d.de.desc, d.en.desc)}</p>
                                            </div>
                                            {d.type === "link" ? (
                                                <a
                                                    href={d.url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    data-testid={d.testid || "dealer-download-pdf-link"}
                                                    className="inline-flex shrink-0 items-center gap-2 rounded-full bg-red-700 hover:bg-red-800 text-white text-sm font-semibold px-5 py-2.5 transition-colors"
                                                >
                                                    {d.ctaDe ? t(d.ctaDe, d.ctaEn) : t("PDF öffnen", "Open PDF")}
                                                    <FileDown className="h-4 w-4" />
                                                </a>
                                            ) : d.type === "route" ? (
                                                <Link
                                                    to={d.url}
                                                    data-testid={d.testid}
                                                    className="inline-flex shrink-0 items-center gap-2 rounded-full bg-red-700 hover:bg-red-800 text-white text-sm font-semibold px-5 py-2.5 transition-colors"
                                                >
                                                    {t(d.de.cta, d.en.cta)}
                                                    {(() => { const Ico = d.icon || BookOpen; return <Ico className="h-4 w-4" />; })()}
                                                </Link>
                                            ) : (
                                                <a
                                                    href={`mailto:${d.mailto}?subject=${encodeURIComponent(`${t(d.de.subject, d.en.subject)} ${session.kdn}`)}`}
                                                    data-testid="dealer-price-request-link"
                                                    className="inline-flex shrink-0 items-center gap-2 rounded-full border border-slate-300 hover:border-red-400 hover:text-red-700 text-slate-700 text-sm font-semibold px-5 py-2.5 transition-colors"
                                                >
                                                    {t("Anfrage senden", "Send request")}
                                                    <Mail className="h-4 w-4" />
                                                </a>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ) : (
                            <form
                                onSubmit={login}
                                data-testid="dealer-login-form"
                                className="rounded-2xl bg-white border border-slate-200 shadow-sm p-7"
                            >
                                <label htmlFor="kundennummer" className="block text-[11px] font-mono uppercase tracking-widest text-slate-500">
                                    {t("Kundennummer", "Customer number")}
                                </label>
                                <input
                                    id="kundennummer"
                                    type="text"
                                    inputMode="numeric"
                                    autoComplete="off"
                                    placeholder="xxxxxxxx"
                                    value={kdnInput}
                                    onChange={(e) => setKdnInput(e.target.value)}
                                    data-testid="dealer-login-input"
                                    className="mt-2 w-full rounded-lg border border-slate-300 focus:border-red-500 focus:ring-2 focus:ring-red-100 outline-none px-4 py-3 font-mono text-lg tracking-wider text-slate-900"
                                />
                                {error && (
                                    <p role="alert" data-testid="dealer-login-error" className="mt-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3">
                                        {error}
                                    </p>
                                )}
                                <button
                                    type="submit"
                                    disabled={!kdnInput.trim()}
                                    data-testid="dealer-login-submit"
                                    className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-full bg-red-700 hover:bg-red-800 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold px-6 py-3.5 transition-colors"
                                >
                                    <KeyRound className="h-4 w-4" />
                                    {t("Anmelden", "Sign in")}
                                </button>
                                <p className="mt-4 text-xs text-slate-500 leading-relaxed">
                                    {t("Nur für Fachhändler. Die Sitzung endet automatisch nach 12 Stunden.", "Trade dealers only. The session ends automatically after 12 hours.")}
                                </p>
                            </form>
                        )}
                    </Reveal>
                </div>
            </div>
        </section>
    );
}
